import {computed, inject, Injectable, signal} from '@angular/core';
import {NzI18nService, en_US, zh_CN} from 'ng-zorro-antd/i18n';
import {TranslocoService} from '@jsverse/transloco';
import {firstValueFrom} from 'rxjs';
import {AppLanguage, AVAILABLE_LANGS, I18N_STORAGE_KEY} from './i18n.config';

@Injectable({providedIn: 'root'})
export class I18nService {
  private readonly translocoService = inject(TranslocoService);
  private readonly nzI18nService = inject(NzI18nService);

  readonly activeLanguage = signal<AppLanguage>('en-US');
  readonly activeLanguageLabel = computed(() => (this.activeLanguage() === 'zh-CN' ? '简体中文' : 'English'));

  async init(): Promise<void> {
    const stored = this.readStoredLanguage();
    const browserLanguage = typeof navigator === 'undefined' ? null : navigator.language;
    await this.applyLanguage(this.normalizeLanguage(stored ?? browserLanguage), false);
  }

  async setLanguage(language: string): Promise<void> {
    await this.applyLanguage(this.normalizeLanguage(language), true);
  }

  private async applyLanguage(language: AppLanguage, persist: boolean): Promise<void> {
    await firstValueFrom(this.translocoService.load(language));
    this.activeLanguage.set(language);
    this.translocoService.setActiveLang(language);
    this.nzI18nService.setLocale(language === 'zh-CN' ? zh_CN : en_US);

    if (persist) {
      this.writeStoredLanguage(language);
    }
  }

  private normalizeLanguage(language: string | null | undefined): AppLanguage {
    const normalized = (language ?? 'en-US').trim();
    if ((AVAILABLE_LANGS as readonly string[]).includes(normalized)) {
      return normalized as AppLanguage;
    }
    return normalized.toLowerCase().startsWith('zh') ? 'zh-CN' : 'en-US';
  }

  private readStoredLanguage(): string | null {
    try {
      return globalThis.localStorage?.getItem(I18N_STORAGE_KEY) ?? null;
    } catch {
      return null;
    }
  }

  private writeStoredLanguage(language: AppLanguage): void {
    try {
      globalThis.localStorage?.setItem(I18N_STORAGE_KEY, language);
    } catch {
      // Ignore storage access issues (e.g. private browsing).
    }
  }
}
